import {baseUri} from '@jetbrains/teamcity-ui'
import type {KeyValue, QueryParams} from '@jetbrains/teamcity-ui/dist/utils'
import {objectToQuery, resolveRelative} from '@jetbrains/teamcity-ui/dist/utils'
import {createBrowserHistory} from 'history'
import type {Location, NavigateFunction, Params, PathMatch} from 'react-router'
import {matchPath, parsePath, useLocation, useNavigate} from 'react-router'

import {ChangePageTabName} from '../components/packages/Changes/ChangeDetailsTabs/ChangeDetailsTabs.types'
import withHook from '../hocs/withHook'
import type {
  ActiveEntityURLProps,
  AgentId,
  AgentPoolId,
  AgentTypeId,
  BuildId,
  BuildTypeId,
  ChangeId,
  Enhancer,
  FederationServerId,
  PipelineId,
  ProjectId,
  TestId,
} from '../types'
import {stringifyId} from '../types'
import {isCreateBuildTypeNewUiEnabled, isCreateProjectNewUiEnabled} from '../utils/featureToggles'
import {mergeSearchParams, queryToObject} from '../utils/queryParams'
import {parseURL, resolveRelativeCustomBase} from '../utils/url'

import SharedRoutes from './shared-routes.json'

export const browserHistory = createBrowserHistory()

// Remove trailing slash, ensure leading slash
const basePath = parseURL(baseUri)
  .pathname.replace(/\/$/g, '')
  .replace(/^[^\/]/, '/$&')
export const Routes = {...SharedRoutes, BASE: `${basePath}/*`}
const AGENTS_SCREEN_ROUTES = [
  Routes.AGENTS_OVERVIEW,
  Routes.FAVORITE_AGENT_POOLS,
  Routes.DISCONNECTED_AGENTS_OVERVIEW,
  Routes.AGENT,
  Routes.AGENT_POOL,
  Routes.CLOUD_IMAGE,
  Routes.AGENTS_UNAUTHORIZED,
  Routes.AGENTS,
]
export default Routes
export const getBaseRoute = (route: string): string => Routes.BASE.replace('*', route)
export const matchRoute = (route: string | null | undefined, pathname: string): PathMatch | null =>
  route == null ? null : matchPath(getBaseRoute(route), pathname)
export const getHref = (
  route: string,
  params: KeyValue<string, string> = {},
  hash?: string | null,
): string =>
  getBaseRoute(route)
    .replace(/:([^\/]*)/g, (_, param) => params[param] ?? '')
    .concat(hash != null ? `#${hash}` : '')
export const ALL_PROJECTS_HASH = 'all-projects'
export const getProjectHref = (projectId: ProjectId, isAllProjects?: boolean): string =>
  getHref(
    Routes.PROJECT,
    {
      projectId: stringifyId(projectId),
    },
    isAllProjects === true ? ALL_PROJECTS_HASH : null,
  )
export const getFavoriteProjectsHref = (): string => getBaseRoute(Routes.FAVORITE_PROJECTS)
export const getFavoriteBuildsHref = (): string => getBaseRoute(Routes.FAVORITE_BUILDS)
export const getFavoriteAgentPoolsHref = (): string => getBaseRoute(Routes.FAVORITE_AGENT_POOLS)
export const getBuildTypeHref = (buildTypeId?: BuildTypeId, isAllProjects?: boolean): string =>
  getHref(
    Routes.BUILD_TYPE,
    {
      buildTypeId: stringifyId(buildTypeId),
    },
    isAllProjects === true ? ALL_PROJECTS_HASH : null,
  )
export const getBuildHref = (
  buildId: BuildId | string,
  buildTypeId?: BuildTypeId | null | undefined,
  isAllProjects?: boolean | null | undefined,
): string =>
  getHref(
    buildTypeId != null ? Routes.BUILD : Routes.BUILD_UNKNOWN_BUILDTYPE,
    {
      buildId: stringifyId(buildId),
      buildTypeId: stringifyId(buildTypeId),
    },
    isAllProjects === true ? ALL_PROJECTS_HASH : null,
  )
export const getCompareBuildHref = (sourceId: BuildId): string =>
  getHref(Routes.COMPARE_BUILDS, {
    buildIdSource: stringifyId(sourceId),
  })
export const getPipelineHref = (pipelineId: PipelineId, pipelineRunId?: string | null): string => {
  if (pipelineId && pipelineRunId != null) {
    return getHref(Routes.PIPELINE_RUN, {pipelineId, pipelineRunId})
  }
  return getHref(Routes.PIPELINE, {pipelineId})
}
export const getOverviewHref = ({
  projectId,
  buildTypeId,
  pipelineId,
  buildId,
  runId,
  isAllProjects,
}: ActiveEntityURLProps): string => {
  if (pipelineId != null) {
    return getPipelineHref(pipelineId, runId)
  }

  if (buildId != null) {
    return getBuildHref(buildId, buildTypeId, isAllProjects)
  }

  if (buildTypeId != null) {
    return getBuildTypeHref(buildTypeId, isAllProjects)
  }

  if (projectId != null) {
    return getProjectHref(projectId, isAllProjects)
  }

  return getFavoriteProjectsHref()
}

export const ALL_POOLS_HASH = 'all-pools'
export const getAgentsHref = (): string => getHref(Routes.AGENTS, {})
export const getAgentsOverviewHref = (): string => getHref(Routes.AGENTS_OVERVIEW, {})
export const getDisconnectedAgentsOverviewHref = (): string =>
  getHref(Routes.DISCONNECTED_AGENTS_OVERVIEW, {})
export const getUnauthorizedAgentsHref = (): string => getHref(Routes.AGENTS_UNAUTHORIZED, {})
export const getAgentHref = (agentId: AgentId | null | undefined, isAllPools?: boolean): string =>
  getHref(
    Routes.AGENT,
    {
      agentId: stringifyId(agentId),
    },
    isAllPools === true ? ALL_POOLS_HASH : null,
  )
export const getAgentPoolHref = (agentPoolId?: AgentPoolId | null, isAllPools?: boolean): string =>
  getHref(
    Routes.AGENT_POOL,
    {
      agentPoolId: stringifyId(agentPoolId),
    },
    isAllPools === true ? ALL_POOLS_HASH : null,
  )
export const getAgentTypeHref = (
  agentTypeId: AgentTypeId | null | undefined,
  isAllPools?: boolean,
): string =>
  getHref(
    Routes.CLOUD_IMAGE,
    {
      agentTypeId: stringifyId(agentTypeId),
    },
    isAllPools === true ? ALL_POOLS_HASH : null,
  )
export const getTestHistoryHref = (
  testId: TestId | null | undefined,
  params: QueryParams,
): string =>
  `${getHref(Routes.TEST, {
    testId: stringifyId(testId),
  })}?${objectToQuery(params)}`

export const getChangeHref = ({
  changeId,
  buildTypeId,
  personal,
  tab,
}: {
  changeId: ChangeId
  buildTypeId?: BuildTypeId | null | undefined
  personal?: boolean | null | undefined
  tab?: string | null | undefined
}): string =>
  `${getHref(Routes.CHANGE, {changeId: stringifyId(changeId)})}?${objectToQuery({
    personal: personal != null ? String(personal) : null,
    buildTypeId: buildTypeId != null ? stringifyId(buildTypeId) : null,
    tab: tab ?? ChangePageTabName.FILES,
  })}`

export const getChangesHref = (): string => getHref(Routes.CHANGES)

export const getPipelinesHref = (): string => getHref(Routes.ALL_PIPELINES)

export const getClassicOverviewHrefWithCustomBase = ({
  projectId,
  buildTypeId,
  pipelineId,
  serverId,
  isAdmin = false,
  isTemplate = false,
}: {
  projectId?: ProjectId | null | undefined
  buildTypeId?: BuildTypeId | null | undefined
  pipelineId?: PipelineId | null
  serverId: FederationServerId
  isAdmin?: boolean
  isTemplate?: boolean
}): string => {
  if (buildTypeId) {
    return resolveRelativeCustomBase(
      stringifyId(serverId),
      isAdmin ? '/admin/editBuild.html' : '/viewType.html',
      isAdmin
        ? {
            id: `${isTemplate ? 'template' : 'buildType'}:${stringifyId(buildTypeId)}`,
          }
        : {
            buildTypeId: stringifyId(buildTypeId),
          },
    )
  }

  if (pipelineId) {
    return resolveRelativeCustomBase(
      stringifyId(serverId),
      isAdmin ? '/admin/editProject.html' : '/project.html',
      {
        pipelineId,
      },
    )
  }

  if (projectId) {
    return resolveRelativeCustomBase(
      stringifyId(serverId),
      isAdmin ? '/admin/editProject.html' : '/project.html',
      {
        projectId: stringifyId(projectId),
      },
    )
  }

  return stringifyId(serverId)
}
export const getQueueHref = (): string => getHref(Routes.QUEUE, {})

export const getInvestigationsHref = (): string => getHref(Routes.INVESTIGATIONS)

export type LocationProps = {
  location: Location
  navigate: NavigateFunction
}
export const withLocation: Enhancer<LocationProps, any> = withHook(() => ({
  location: useLocation(),
  navigate: useNavigate(),
}))
export const getHrefWithQueryParams = (
  location: Location,
  href?: string | null,
  params: QueryParams | ((prevParams: QueryParams) => QueryParams) = p => p,
  hash?: string,
  merge?: boolean,
): string => {
  const base = href != null ? parsePath(href) : location
  let search: string
  if (merge === true && typeof params !== 'function') {
    const withBase = mergeSearchParams(location.search, queryToObject(base.search ?? ''))
    search = mergeSearchParams(withBase, params)
  } else {
    search = objectToQuery(
      typeof params === 'function' ? params(queryToObject(location.search ?? {})) : params,
    )
  }
  return browserHistory.createHref({
    ...base,
    search,
    ...(hash != null ? {hash: `#${hash}`} : null),
  })
}
//
// export const useHrefWithQueryParams = (
//   href: ?string,
//   params: ?QueryParams | (QueryParams => QueryParams),
//   hash: ?string,
// ) => useLocationSelector(location => getHrefWithQueryParams(location, href, params, hash))
export const isQueueScreen = (pathname: string): boolean =>
  matchRoute(Routes.QUEUE, pathname) != null

// It's not possible to solve this issue with the `react-router`
export function isAgentsScreen(pathname: string) {
  return Boolean(AGENTS_SCREEN_ROUTES.find(route => matchRoute(route, pathname)))
}

export const isProjectCreationScreen = (pathname: string): boolean =>
  matchRoute(Routes.CREATE_PROJECT, pathname) != null

export const isPipelineCreationScreen = (pathname: string): boolean =>
  matchRoute(Routes.CREATE_PIPELINE, pathname) != null

export const isPipelinesScreen = (pathname: string): boolean =>
  matchRoute(Routes.PIPELINES, pathname) != null ||
  matchRoute(Routes.PIPELINE_PAGES, pathname) != null

export const isPipelinesEditScreen = (queryParams: QueryParams): boolean =>
  queryParams.edit === 'true'

export const isPipelinesRunScreen = (pathname: string, params: Readonly<Params>): boolean =>
  matchRoute(Routes.PIPELINE_RUN, pathname) != null && !!params.pipelineRunId

const matchAny = (routes: string[], pathname: string) =>
  routes.some(route => matchRoute(route, pathname) != null)

const PROJECT_DOMAIN_BREADCRUMBS_ROUTES = [
  Routes.PROJECT,
  Routes.BUILD_TYPE,
  Routes.BUILD,
  Routes.BUILD_UNKNOWN_BUILDTYPE,
]

export const isProjectDomainBreadcrumbsScreen = (pathname: string) =>
  matchAny(PROJECT_DOMAIN_BREADCRUMBS_ROUTES, pathname)

const AGENT_DOMAIN_BREADCRUMBS_ROUTES = [Routes.AGENT, Routes.AGENT_POOL, Routes.CLOUD_IMAGE]
export const isAgentDomainBreadcrumbsScreen = (pathname: string) =>
  matchAny(AGENT_DOMAIN_BREADCRUMBS_ROUTES, pathname)

const PIPELINE_DOMAIN_NO_BREADCRUMBS_ROUTES = [
  Routes.ALL_PIPELINES,
  Routes.CREATE_PIPELINE,
  Routes.WELCOME_PIPELINES,
]
export const isPipelineDomainBreadcrumbsScreen = (pathname: string) =>
  isPipelinesScreen(pathname) && !matchAny(PIPELINE_DOMAIN_NO_BREADCRUMBS_ROUTES, pathname)

const PROJECT_SIDEBAR_ROUTES = [
  Routes.FAVORITE_PROJECTS,
  Routes.FAVORITE_BUILDS,
  Routes.PROJECT,
  Routes.BUILD_TYPE,
  Routes.BUILD,
  Routes.BUILD_UNKNOWN_BUILDTYPE,
  Routes.COMPARE_BUILDS,
  Routes.TEST,
  Routes.ALL_PIPELINES,
  Routes.PIPELINE_PAGES,
]
export const isProjectSidebarScreen = (pathname: string) =>
  matchAny(PROJECT_SIDEBAR_ROUTES, pathname)
export const isScreenWithSidebar = (pathname: string) =>
  isQueueScreen(pathname) || isAgentsScreen(pathname) || isProjectSidebarScreen(pathname)
export const isScreenWithHeader = (pathname: string) => !isProjectCreationScreen(pathname)

const SAKURA_UI_ONLY_ROUTES = [
  Routes.COMPARE_BUILDS,
  Routes.GUIDES,
  Routes.PIPELINES,
  Routes.PIPELINE_PAGES,
  Routes.NOTIFICATIONS,
  Routes.CREATE_PROJECT,
]
export const isSakuraUIOnlyScreen = (pathname: string): boolean =>
  matchAny(SAKURA_UI_ONLY_ROUTES, pathname)

const CENTERED_CONTENT_ROUTES = [Routes.NOTIFICATIONS]
export const isCenteredContentScreen = (pathname: string): boolean =>
  matchAny(CENTERED_CONTENT_ROUTES, pathname)

export enum CreateType {
  PROJECT = 'createProjectMenu',
  BUILD_TYPE = 'createBuildTypeMenu',
}

export function generateCreateEntityHref({
  projectId,
  type,
  params,
  hash,
}: {
  projectId: ProjectId
  type: CreateType
  params?: QueryParams
  hash?: string
}) {
  return resolveRelative(
    '/admin/createObjectMenu.html',
    {
      projectId: stringifyId(projectId),
      showMode: type,
      cameFromUrl: location.href,
      ...params,
    },
    hash,
  )
}

export function getCreateBuildTypeHref(projectId: ProjectId) {
  if (isCreateBuildTypeNewUiEnabled) {
    return `${getHref(Routes.CREATE_PROJECT)}?projectId=${projectId}&setup=build&cameFromUrl=${encodeURIComponent(location.pathname + location.search)}`
  } else {
    return generateCreateEntityHref({projectId, type: CreateType.BUILD_TYPE})
  }
}

export function getCreatePipelineHref(projectId: ProjectId) {
  if (isCreateBuildTypeNewUiEnabled) {
    return `${getHref(Routes.CREATE_PROJECT)}?projectId=${projectId}&setup=pipeline&cameFromUrl=${encodeURIComponent(location.pathname + location.search)}`
  } else {
    return `${getHref(Routes.CREATE_PIPELINE)}?projectId=${projectId}`
  }
}

export function getCreateProjectHref(projectId: ProjectId) {
  if (isCreateProjectNewUiEnabled) {
    return `${getHref(Routes.CREATE_PROJECT)}?projectId=${projectId}&cameFromUrl=${location.pathname + location.search}`
  } else {
    return generateCreateEntityHref({projectId, type: CreateType.PROJECT})
  }
}

export function getEditUrlFormats() {
  return {
    buildTypeUrlFormat: resolveRelative('/admin/editBuild.html?id=buildType:{id}'),
    projectUrlFormat: resolveRelative('/admin/editProject.html?projectId={id}'),
  }
}
